import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Award, Heart, Leaf, Users, MapPin, FlaskConical, Flower2, ArrowRight } from 'lucide-react';

export default function AboutPage() {
  const { t } = useLanguage();
  const navigate = useNavigate();

  const values = [
    {
      icon: <Award className="w-10 h-10 text-primary" />,
      title: t('about.values.quality.title'),
      description: t('about.values.quality.desc')
    },
    {
      icon: <Users className="w-10 h-10 text-primary" />,
      title: t('about.values.tradition.title'),
      description: t('about.values.tradition.desc')
    },
    {
      icon: <Leaf className="w-10 h-10 text-primary" />,
      title: t('about.values.nature.title'),
      description: t('about.values.nature.desc')
    },
    {
      icon: <Heart className="w-10 h-10 text-primary" />,
      title: t('about.values.family.title'),
      description: t('about.values.family.desc')
    }
  ];

  const process = [
    {
      icon: <MapPin className="w-8 h-8 text-primary" />,
      title: t('about.process.step1.title'),
      description: t('about.process.step1.desc'),
      number: '01'
    },
    {
      icon: <Flower2 className="w-8 h-8 text-primary" />,
      title: t('about.process.step2.title'),
      description: t('about.process.step2.desc'),
      number: '02'
    },
    {
      icon: <Award className="w-8 h-8 text-primary" />,
      title: t('about.process.step3.title'),
      description: t('about.process.step3.desc'),
      number: '03'
    },
    {
      icon: <FlaskConical className="w-8 h-8 text-primary" />,
      title: t('about.process.step4.title'),
      description: t('about.process.step4.desc'),
      number: '04'
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-primary to-[hsl(42,100%,65%)] text-white py-20">
        <div className="container-custom px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center animate-fade-in">
            <Badge className="mb-4 bg-white/20 text-white border-white/30">
              <Leaf className="w-4 h-4 mr-2" />
              {t('about.pageTitle')}
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              {t('about.hero.title')}
            </h1>
            <p className="text-lg text-white/90">
              {t('about.hero.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="section-padding bg-background">
        <div className="container-custom px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12 animate-fade-in">
              <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-foreground">
                {t('about.story.title')}
              </h2>
            </div>
            
            {/* Intro Text */}
            <Card className="mb-12 bg-secondary/30 border-primary/20">
              <CardContent className="p-8">
                <p className="text-muted-foreground leading-relaxed text-lg italic">
                  {t('about.story.intro')}
                </p>
              </CardContent>
            </Card>
            
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div className="space-y-6">
                <img 
                  src="https://images.unsplash.com/photo-1587049352851-8d4e89133924" 
                  alt="Beekeeping"
                  className="w-full h-64 object-cover rounded-lg shadow-lg"
                />
                <p className="text-muted-foreground leading-relaxed">
                  {t('about.story.content1')}
                </p>
              </div>
              
              <div className="space-y-6">
                <p className="text-muted-foreground leading-relaxed">
                  {t('about.story.content2')}
                </p>
                <img 
                  src="https://customer-assets.emergentagent.com/job_static-website-1/artifacts/o4fsg3l6_Tegla%20Meda%20i%20sace.jpg" 
                  alt="Honey and Honeycomb"
                  className="w-full h-64 object-cover rounded-lg shadow-lg"
                />
              </div>
            </div>
            
            {/* Third Image */}
            <div className="mb-12">
              <img 
                src="https://customer-assets.emergentagent.com/job_static-website-1/artifacts/9d4m9taj_gallery-3.jpg" 
                alt="Beekeeping Tradition"
                className="w-full h-96 object-cover rounded-lg shadow-xl"
              />
            </div>
            
            <Card className="bg-secondary/30 border-primary/20">
              <CardContent className="p-8">
                <p className="text-muted-foreground leading-relaxed text-lg">
                  {t('about.story.content3')}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-padding bg-secondary/30">
        <div className="container-custom px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-foreground">
              {t('about.values.title')}
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <Card 
                key={index}
                className="card-hover bg-card border-border shadow-md"
                style={{
                  animationDelay: `${index * 100}ms`,
                  animation: 'slideUp 0.6s ease-out forwards',
                  opacity: 0
                }}
              >
                <CardContent className="p-6 text-center flex flex-col items-center">
                  <div className="mb-4 p-4 bg-primary/10 rounded-full">
                    {value.icon}
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-foreground">
                    {value.title}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {value.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section-padding bg-background">
        <div className="container-custom px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-foreground">
              {t('about.process.title')}
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {process.map((step, index) => (
              <Card 
                key={index}
                className="relative bg-card border-border shadow-md overflow-hidden"
              >
                <div className="absolute top-0 right-0 text-8xl font-bold text-primary/5">
                  {step.number}
                </div>
                <CardContent className="p-6 relative z-10">
                  <div className="mb-4 p-3 bg-primary/10 rounded-full inline-block">
                    {step.icon}
                  </div>
                  <h3 className="text-lg font-semibold mb-3 text-foreground">
                    {step.title}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {step.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Commitment Section */}
      <section className="section-padding bg-gradient-to-r from-primary to-[hsl(42,100%,65%)] text-white">
        <div className="container-custom px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">
              {t('about.commitment.title')}
            </h2>
            <p className="text-lg mb-8 opacity-90 leading-relaxed">
              {t('about.commitment.text')}
            </p>
            <Button 
              onClick={() => navigate('/products')}
              size="lg"
              className="bg-white text-primary hover:bg-white/90 shadow-lg font-semibold"
            >
              {t('products.viewAll')}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
